import {
  ChevronRight,
  Close,
  ContentCopy,
  ContentPaste,
  Description,
  HelpOutline,
  History,
  MenuBook,
  Send,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AudioIframe from './AudioIframe';
import DynamicAssessmentQuestions from './DynamicAssessmentQuestions';
import QcClassificationTagField from './QcClassificationTagField';
import QcTranscriptDiffPanel from './QcTranscriptDiffPanel';
import TranscriptInlineChipEditor from './TranscriptInlineChipEditor';
import { useTranscriptClipboard } from './useTranscriptClipboard';
import PasteConfirmDialog from './PasteConfirmDialog';
import { isRtlLanguage } from '../../utils/rtlLanguages';

export default function QcAssessmentDesktopLayout({
  onClose,
  taskMeta,
  sortedQuestions,
  readOnly,
  linkedTranscriptQuestionIdSet,
  linkedTranscriptQuestionIds = [],
  onPlainTranscriptFromLinkedField,
  transcriptSegments,
  onTranscriptSegmentsChange,
  transcriptInputRef,
  classificationTags = [],
  onAddClassificationTag,
  classificationTagOptions = [],
  showTranscriptFields = true,
  vendorPlainForDiff = '',
  transcriptChangePercentage = 0,
  transcriptDiffExpanded = false,
  onTranscriptDiffExpandedChange,
  onTranscriptEditorCommit,
  onLinkedTranscriptFieldCommitted,
  onSaveAndNext,
  onSubmitAndNext,
  onSubmitAndClose,
  isSaving = false,
  isSubmitting = false,
  reviewer = false,
  onReviewApprove,
  onReviewApproveNext,
  onReviewReject,
  onReviewSkip,
  isPendingReview = false,
}) {
  const showStandAloneClassification =
    !showTranscriptFields &&
    Array.isArray(classificationTagOptions) &&
    classificationTagOptions.length > 0;

  const transcriptDir = isRtlLanguage(taskMeta.language) ? 'rtl' : 'ltr';

  const {
    clipboardMessage,
    handleCopyVendorTranscript,
    handlePasteIntoUpdatedTranscript,
    pasteConfirmReasons,
    confirmPendingPaste,
    cancelPendingPaste,
  } = useTranscriptClipboard({
    vendorText: taskMeta.originalTranscript,
    readOnly,
    transcriptInputRef,
    transcriptSegments,
    onTranscriptEditorCommit,
  });

  const reqDisplay =
    taskMeta.requestId && taskMeta.requestId !== '—'
      ? `#${String(taskMeta.requestId).replace(/^#/, '')}`
      : '#—';

  const durationLabel =
    taskMeta.durationSec != null &&
    Number.isFinite(Number(taskMeta.durationSec))
      ? `${Number(taskMeta.durationSec).toFixed(2)}s`
      : null;

  return (
    <Box
      className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f8f9ff] font-[Inter,system-ui,sans-serif] text-[#0d1c2e] antialiased dark:bg-gray-950 dark:text-gray-100"
      sx={{ height: '100%', minHeight: 0, maxHeight: '100dvh' }}
    >
      <Box
        component="header"
        className="flex shrink-0 items-center justify-between border-b border-[rgba(197,197,212,0.3)] bg-white px-3 py-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <Box className="flex min-w-0 flex-1 flex-wrap items-center gap-4 md:gap-6">
          <Box className="flex items-center gap-3">
            <Typography
              className="font-[Manrope,Inter,sans-serif] text-lg font-extrabold tracking-tighter text-[#24389c] dark:text-[#9aacf0]"
              sx={{ fontFamily: 'Manrope, Inter, sans-serif' }}
            >
              Assessment Details
            </Typography>
          </Box>
          <Box className="hidden h-4 w-px bg-[rgba(197,197,212,0.3)] dark:bg-gray-600 md:block" />
          <Box
            component="nav"
            className="hidden items-center gap-1 text-[11px] font-medium tracking-wide text-[rgba(69,70,82,0.6)] dark:text-gray-400 md:flex"
          >
            <span className="cursor-pointer hover:underline">QC</span>
            <ChevronRight sx={{ fontSize: 14, color: 'currentColor' }} />
            <span className="cursor-pointer hover:underline">Audio</span>
            <ChevronRight sx={{ fontSize: 14, color: 'currentColor' }} />
            <span className="font-bold text-[#24389c] dark:text-[#8b9fe8]">
              {reqDisplay}
            </span>
          </Box>
          <Box className="hidden h-4 w-px bg-[rgba(197,197,212,0.3)] dark:bg-gray-600 xl:block" />
          <Box className="hidden flex-wrap items-center gap-4 text-[11px] xl:flex">
            <Box className="flex gap-1">
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">
                File:
              </span>
              <span
                className="font-semibold text-[#0d1c2e] dark:text-gray-100"
                title={taskMeta.fileName}
              >
                {taskMeta.fileNameShort}
              </span>
            </Box>
            {durationLabel ? (
              <Box className="flex gap-1">
                <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">
                  Dur:
                </span>
                <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">
                  {durationLabel}
                </span>
              </Box>
            ) : null}
            <Box className="flex gap-1">
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">
                State:
              </span>
              <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">
                {taskMeta.state}
              </span>
            </Box>
            <Box className="flex gap-1">
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">
                Dist:
              </span>
              <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">
                {taskMeta.district}
              </span>
            </Box>
            <Box className="flex gap-1">
              <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">
                Lang:
              </span>
              <span className="font-semibold text-[#0d1c2e] dark:text-gray-100">
                {taskMeta.language}
              </span>
            </Box>
            {taskMeta.assignedTo ? (
              <Box className="flex gap-1">
                <span className="text-[10px] font-medium uppercase text-[rgba(69,70,82,0.6)] dark:text-gray-500">
                  Assignee:
                </span>
                <span
                  className="max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-[#0d1c2e] dark:text-gray-100"
                  title={taskMeta.assignedTo}
                >
                  {taskMeta.assignedTo}
                </span>
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box className="flex shrink-0 items-center gap-1">
          <Box className="mx-1 h-4 w-px bg-[rgba(197,197,212,0.3)] dark:bg-gray-600" />
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close assessment"
            className="text-[rgba(69,70,82,0.7)] hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-950/40 dark:hover:text-red-400"
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      <Box
        component="main"
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3 md:flex-row"
      >
        <Box className="custom-scrollbar flex w-full flex-col gap-3 overflow-y-auto md:w-[40%] md:max-w-[40%]">
          <Box className="relative h-[400px] min-h-[400px] overflow-hidden rounded-xl border border-[rgba(197,197,212,0.2)] bg-transparent shadow-sm dark:border-gray-700">
            {taskMeta.filePath ? (
              <Box className="absolute inset-0">
                <AudioIframe
                  audioFileURL={taskMeta.filePath}
                  title="Spectrogram"
                  height={400}
                />
              </Box>
            ) : (
              <Box className="flex h-full min-h-[400px] items-center justify-center bg-[rgba(197,197,212,0.08)] px-4 dark:bg-white/5">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  No file URL for spectrogram preview.
                </Typography>
              </Box>
            )}
          </Box>

          {showTranscriptFields ? (
            <Box
              className="flex min-h-[200px] flex-1 flex-col rounded-xl border border-[rgba(197,197,212,0.2)] shadow-sm dark:border-gray-700"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                alignSelf: 'flex-start',
                width: '100%',
              }}
            >
              <Box className="flex items-center justify-between border-b border-[rgba(197,197,212,0.1)] p-3 dark:border-gray-700">
                <Box className="flex items-center gap-2">
                  <Description className="text-lg text-[#24389c]" />
                  <Typography
                    className="font-[Manrope] text-[10px] font-bold uppercase tracking-[0.15em] text-[#454652] dark:text-gray-400"
                    sx={{ fontFamily: 'Manrope, Inter, sans-serif' }}
                  >
                    Vendor Transcript
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                  className="text-[10px] font-bold uppercase tracking-wider text-[#24389c] hover:bg-[rgba(36,56,156,0.05)]"
                  onClick={() => void handleCopyVendorTranscript()}
                >
                  Copy
                </Button>
              </Box>
              <Box
                dir={transcriptDir}
                className="custom-scrollbar max-h-[280px] overflow-y-auto p-4 text-sm leading-relaxed text-[#0d1c2e]/80 dark:text-gray-300"
              >
                {taskMeta.originalTranscript}
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-[rgba(197,197,212,0.2)] bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <Stack
            spacing={1.5}
            className="custom-scrollbar"
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              p: { xs: 1.5, md: 1.75 },
            }}
          >
            {showTranscriptFields ? (
              <Stack component="section" spacing={0.75} sx={{ flexShrink: 0 }}>
                <QcTranscriptDiffPanel
                  expanded={transcriptDiffExpanded}
                  onToggle={onTranscriptDiffExpandedChange}
                  oldValue={vendorPlainForDiff}
                  committedChangePercentage={transcriptChangePercentage}
                  readOnly={readOnly}
                  dir={transcriptDir}
                />
                <Box className="flex flex-wrap items-center gap-1.5">
                  <Box className="h-3 w-0.5 shrink-0 rounded-full bg-[#24389c]" />
                  <Typography
                    className="font-[Manrope] text-xs font-bold text-[#0d1c2e] dark:text-gray-100"
                    sx={{
                      fontFamily: 'Manrope, Inter, sans-serif',
                      lineHeight: 1.2,
                    }}
                  >
                    Transcript Update
                    {transcriptChangePercentage > 0 ? (
                      <Typography
                        component="span"
                        sx={{
                          ml: 0.75,
                          fontSize: 'inherit',
                          fontWeight: 800,
                          color: 'primary.main',
                        }}
                      >
                        (Change: {transcriptChangePercentage.toFixed(2)}%)
                      </Typography>
                    ) : null}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 0.25, sm: 0.5 },
                    alignItems: 'flex-start',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <TranscriptInlineChipEditor
                    ref={transcriptInputRef}
                    id="qc-updated-transcript-editor"
                    segments={transcriptSegments}
                    onSegmentsChange={onTranscriptSegmentsChange}
                    onCommit={onTranscriptEditorCommit}
                    readOnly={readOnly}
                    minRows={3}
                    placeholder="Enter updated transcription..."
                    aria-label="Updated transcript"
                    dir={transcriptDir}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <Tooltip title="Paste from clipboard" arrow>
                    <span>
                      <IconButton
                        type="button"
                        size="small"
                        aria-label="Paste from clipboard"
                        disabled={readOnly}
                        onClick={() => void handlePasteIntoUpdatedTranscript()}
                        sx={{
                          color: '#24389c',
                          mt: { xs: 0.25, sm: 0.5 },
                          flexShrink: 0,
                        }}
                      >
                        <ContentPaste sx={{ fontSize: 20 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {clipboardMessage ? (
                  <Typography
                    role="status"
                    variant="caption"
                    sx={{
                      display: 'block',
                      color:
                        clipboardMessage.severity === 'error'
                          ? 'error.main'
                          : clipboardMessage.severity === 'success'
                            ? 'success.main'
                            : 'text.secondary',
                    }}
                  >
                    {clipboardMessage.text}
                  </Typography>
                ) : null}
                <PasteConfirmDialog
                  reasons={pasteConfirmReasons}
                  onConfirm={confirmPendingPaste}
                  onCancel={cancelPendingPaste}
                />
                <QcClassificationTagField
                  id="qc-desktop-classification-tag"
                  options={classificationTagOptions}
                  selectedTags={showTranscriptFields ? [] : classificationTags}
                  onAddTag={onAddClassificationTag}
                  readOnly={readOnly}
                  size="small"
                  sx={(theme) => ({
                    mt: 0,
                    '& .MuiOutlinedInput-root': {
                      minHeight: theme.spacing(4.5),
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(197,197,212,0.6)',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0.75,
                      fontSize: theme.typography.pxToRem(13),
                    },
                  })}
                />
              </Stack>
            ) : null}

            {showTranscriptFields || showStandAloneClassification ? (
              <Divider sx={{ borderColor: 'divider' }} />
            ) : null}

            <Stack spacing={1.25} sx={{ flexShrink: 0, minWidth: 0 }}>
              <Box className="flex flex-wrap items-center gap-2">
                <Box className="h-4 w-1 shrink-0 rounded-full bg-orange-500" />
                <Typography
                  className="font-[Manrope] text-sm font-bold text-[#0d1c2e] dark:text-gray-100"
                  sx={{ fontFamily: 'Manrope, Inter, sans-serif' }}
                >
                  Assessment Questions
                </Typography>
                {readOnly ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      color: 'primary.light',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Submitted / read-only
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ minWidth: 0, width: '100%' }}>
                <DynamicAssessmentQuestions
                  questions={sortedQuestions}
                  readOnly={readOnly}
                  variant="desktop"
                  linkedTranscriptQuestionIdSet={linkedTranscriptQuestionIdSet}
                  linkedTranscriptQuestionIds={linkedTranscriptQuestionIds}
                  onPlainTranscriptFromLinkedField={
                    onPlainTranscriptFromLinkedField
                  }
                  onLinkedTranscriptFieldCommitted={
                    onLinkedTranscriptFieldCommitted
                  }
                />
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Box
        component="footer"
        className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-[rgba(197,197,212,0.2)] bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900 md:px-5"
      >
        {reviewer ? (
          <>
            <Button onClick={onClose} className="text-xs font-bold uppercase tracking-wider text-[#454652] hover:bg-black/5 dark:text-gray-200 dark:hover:bg-white/10">
              Cancel
            </Button>
            <Box className="flex flex-wrap items-center gap-2 md:gap-3">
              <Button variant="outlined" onClick={onReviewSkip} disabled={isPendingReview} className="text-[#454652] dark:text-gray-200">
                Skip
              </Button>
              <Button variant="outlined" color="error" onClick={onReviewReject} disabled={isPendingReview}>
                Reject
              </Button>
              <Button variant="outlined" color="success" onClick={onReviewApproveNext} disabled={isPendingReview}>
                {isPendingReview ? 'Saving...' : 'Approve & Next'}
              </Button>
              <Button variant="contained" color="success" onClick={onReviewApprove} disabled={isPendingReview}>
                {isPendingReview ? 'Saving...' : 'Approve'}
              </Button>
            </Box>
          </>
        ) : readOnly ? (
          <Button
            onClick={onClose}
            variant="contained"
            className="ml-auto rounded bg-[#24389c] px-6 py-2 text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[#1e3a8a]"
            sx={{ bgcolor: '#24389c', '&:hover': { bgcolor: '#1e3a8a' } }}
          >
            Close
          </Button>
        ) : (
          <>
            <Box className="flex flex-wrap items-center gap-4">
              <Button
                onClick={onClose}
                className="text-xs font-bold uppercase tracking-wider text-[#454652] hover:bg-black/5 dark:text-gray-200 dark:hover:bg-white/10"
              >
                Cancel
              </Button>
              <Box className="hidden h-6 w-px bg-[rgba(197,197,212,0.3)] dark:bg-gray-600 sm:block" />
            </Box>
            <Box className="flex flex-wrap items-center gap-2 md:gap-3">
              <Button
                variant="outlined"
                className="rounded-md border border-[rgba(36,56,156,0.2)] px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#24389c] hover:bg-[rgba(36,56,156,0.05)] dark:border-[rgba(139,159,232,0.45)] dark:text-[#b4c2f5] dark:hover:bg-white/10"
                onClick={onSaveAndNext}
                disabled={isSaving || isSubmitting}
              >
                {isSaving ? 'Saving...' : 'Save & Next'}
              </Button>
              <Button
                variant="outlined"
                className="rounded-md border border-[rgba(36,56,156,0.2)] px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#24389c] hover:bg-[rgba(36,56,156,0.05)] dark:border-[rgba(139,159,232,0.45)] dark:text-[#b4c2f5] dark:hover:bg-white/10"
                onClick={onSubmitAndNext}
                disabled={isSubmitting || isSaving}
              >
                {isSubmitting ? 'Submitting...' : 'Submit & Next'}
              </Button>
              <Button
                variant="contained"
                endIcon={<Send sx={{ fontSize: 18 }} />}
                className="rounded bg-[#24389c] px-6 py-2 text-[11px] font-bold uppercase tracking-widest text-white shadow-sm hover:bg-[#1e3a8a] hover:shadow"
                sx={{
                  bgcolor: '#24389c',
                  '&:hover': { bgcolor: '#1e3a8a' },
                }}
                onClick={onSubmitAndClose}
                disabled={isSubmitting || isSaving}
              >
                {isSubmitting ? 'Submitting...' : 'Submit & Close'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
